"use client";

import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { parseMarkdownImageTitle } from "@/lib/markdown-image-size";

interface MarkdownRendererProps {
	content: string;
	className?: string;
	/** Use admin theme (dark-aware) or public theme (light-only) */
	variant?: "admin" | "public";
}

export function MarkdownRenderer({
	content,
	className = "",
	variant = "public",
}: MarkdownRendererProps) {
	const isAdmin = variant === "admin";

	return (
		<div
			className={`markdown-content markdown-content-${variant} prose prose-lg max-w-none ${isAdmin ? "prose-invert prose-zinc prose-headings:font-display" : ""} ${className}`}
		>
			<ReactMarkdown
				rehypePlugins={[rehypeRaw, rehypeSanitize]}
				remarkPlugins={[remarkGfm]}
				components={{
					h1: ({ node, children, ...props }) => (
						<h1
							className="markdown-heading markdown-heading-1 text-3xl font-bold mt-8 mb-4 tracking-tight"
							{...props}
						>
							{children}
						</h1>
					),
					h2: ({ node, children, ...props }) => (
						<h2
							className="markdown-heading markdown-heading-2 text-2xl font-semibold mt-8 mb-4 tracking-tight"
							{...props}
						>
							{children}
						</h2>
					),
					h3: ({ node, children, ...props }) => (
						<h3
							className="markdown-heading markdown-heading-3 text-xl font-semibold mt-6 mb-3 tracking-tight"
							{...props}
						>
							{children}
						</h3>
					),
					p: ({ node, ...props }) => (
						<p
							className={`markdown-paragraph mb-6 leading-relaxed ${isAdmin ? "text-zinc-300" : "text-zinc-600"}`}
							{...props}
						/>
					),
					a: ({ node, ...props }) => (
						<a
							className={`markdown-link ${isAdmin ? "text-indigo-400" : "text-indigo-600"} hover:underline font-medium`}
							{...props}
						/>
					),
					img: ({ node, alt, title, ...props }) => {
						const imageTitle = parseMarkdownImageTitle(title);

						return (
							// biome-ignore lint/performance/noImgElement: native img needed for markdown content
							<img
								alt={alt || ""}
								className={`markdown-image rounded-md border max-w-full h-auto my-8 shadow-sm ${isAdmin ? "border-zinc-800" : "border-zinc-200"}`}
								height={imageTitle.height}
								title={imageTitle.title}
								width={imageTitle.width}
								{...props}
							/>
						);
					},
					table: ({ node, ...props }) => (
						<div
							className={`markdown-table-wrap overflow-x-auto border my-8 rounded-md ${isAdmin ? "border-zinc-800" : "border-zinc-200"} bg-zinc-50/30`}
						>
							<table
								className="markdown-table w-full text-sm text-left mt-0! mb-2"
								{...props}
							/>
						</div>
					),
					thead: ({ node, ...props }) => (
						<thead
							className={`markdown-thead ${isAdmin ? "bg-zinc-900 border-zinc-800" : "bg-zinc-50 border-zinc-200"} border-b`}
							{...props}
						/>
					),
					tr: ({ node, ...props }) => (
						<tr
							className={`markdown-tr border-b last:border-0 ${isAdmin ? "border-zinc-800" : "border-zinc-100"}`}
							{...props}
						/>
					),
					th: ({ node, ...props }) => (
						<th
							className={`markdown-th px-4 py-3 font-semibold uppercase text-xs tracking-wider ${isAdmin ? "text-zinc-100" : "text-zinc-900"}`}
							{...props}
						/>
					),
					td: ({ node, ...props }) => (
						<td
							className="markdown-td px-4 py-3 font-medium text-zinc-600"
							{...props}
						/>
					),
					ul: ({ node, className, ...props }) => (
						<ul
							className={`markdown-list markdown-list-unordered list-disc pl-6 mb-6 space-y-2 text-zinc-600 ${className || ""}`}
							{...props}
						/>
					),
					ol: ({ node, className, ...props }) => (
						<ol
							className={`markdown-list markdown-list-ordered list-decimal pl-6 mb-6 space-y-2 text-zinc-600 ${className || ""}`}
							{...props}
						/>
					),
					li: ({ node, className, ...props }) => (
						<li
							className={`markdown-list-item ${className || ""}`}
							{...props}
						/>
					),
					blockquote: ({ node, ...props }) => (
						<blockquote
							className={`markdown-blockquote border-l-4 border-indigo-500 pl-6 italic my-8 text-lg py-2 rounded-r-md ${isAdmin ? "text-zinc-300 bg-zinc-800/50" : "text-zinc-700 bg-zinc-50/50"}`}
							{...props}
						/>
					),
					hr: ({ node, ...props }) => <hr className="markdown-hr" {...props} />,
					del: ({ node, ...props }) => (
						<del className="markdown-del" {...props} />
					),
					input: ({ node, ...props }) => (
						<input className="markdown-task-checkbox" {...props} disabled />
					),
					pre: ({ node, ...props }) => (
						<pre className="markdown-code-block" {...props} />
					),
					// biome-ignore lint/suspicious/noExplicitAny: ReactMarkdown component props typing
					code: ({ node, className, children, ...props }: any) => {
						const match = /language-(\w+)/.exec(className || "");
						return match ? (
							<code className={`markdown-code ${className}`} {...props}>
								{children}
							</code>
						) : (
							<code
								className={`markdown-inline-code px-1.5 py-0.5 rounded-md text-sm font-mono font-medium ${isAdmin ? "bg-zinc-800 text-indigo-400" : "bg-zinc-100 text-indigo-600"}`}
								{...props}
							>
								{children}
							</code>
						);
					},
				}}
			>
				{content || "*No content to preview*"}
			</ReactMarkdown>
		</div>
	);
}
